import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PawPrint, Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const empty = { name: "", breed: "", sex: "", birthdate: "", color: "", photo_url: "", pack_role: "", notes: "" };

export default function Dogs() {
  const [dogs, setDogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/dogs").then((r) => setDogs(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Please enter a name"); return; }
    setSaving(true);
    try {
      await api.post("/dogs", form);
      toast.success(`${form.name} added to the pack!`);
      setOpen(false); setForm(empty); load();
    } catch { toast.error("Could not save dog"); }
    setSaving(false);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="dogs-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-head text-4xl font-extrabold tracking-tight">My Dogs</h1>
          <p className="text-muted-foreground mt-2">Profiles for every member of your pack.</p>
        </div>
        <Button data-testid="open-add-dog" onClick={() => setOpen(true)} className="rounded-full">
          <Plus className="h-4 w-4 mr-1" /> Add dog
        </Button>
      </div>

      {dogs.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-14 text-center bg-card/50">
          <PawPrint className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No dogs yet.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {dogs.map((d) => (
            <Link to={`/dogs/${d.id}`} key={d.id} data-testid={`dog-item-${d.id}`}
              className="rounded-2xl bg-card border border-border/70 overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
              <div className="h-40 bg-muted">
                {d.photo_url ? <img src={d.photo_url} alt={d.name} className="h-full w-full object-cover" />
                  : <div className="h-full w-full flex items-center justify-center"><PawPrint className="h-10 w-10 text-muted-foreground/50" /></div>}
              </div>
              <div className="p-4">
                <div className="font-head font-semibold text-lg">{d.name}</div>
                <div className="text-sm text-muted-foreground">{d.breed || "Mixed breed"}</div>
                {d.pack_role && <span className="inline-block mt-2 text-xs bg-accent/20 text-accent-foreground px-2 py-0.5 rounded-full">{d.pack_role}</span>}
              </div>
            </Link>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader><DialogTitle className="font-head">Add a dog</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto scroll-thin pr-1">
            <div className="col-span-2"><Label>Name *</Label><Input data-testid="dog-name" value={form.name} onChange={set("name")} placeholder="Rex" /></div>
            <div><Label>Breed</Label><Input data-testid="dog-breed" value={form.breed} onChange={set("breed")} placeholder="Labrador" /></div>
            <div><Label>Sex</Label><Input data-testid="dog-sex" value={form.sex} onChange={set("sex")} placeholder="Male / Female" /></div>
            <div><Label>Birthdate</Label><Input data-testid="dog-birthdate" type="date" value={form.birthdate} onChange={set("birthdate")} /></div>
            <div><Label>Color</Label><Input data-testid="dog-color" value={form.color} onChange={set("color")} placeholder="Golden" /></div>
            <div className="col-span-2"><Label>Pack role</Label><Input data-testid="dog-role" value={form.pack_role} onChange={set("pack_role")} placeholder="e.g. Leader, Follower" /></div>
            <div className="col-span-2"><Label>Photo URL</Label><Input data-testid="dog-photo" value={form.photo_url} onChange={set("photo_url")} placeholder="https://..." /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea data-testid="dog-notes" value={form.notes} onChange={set("notes")} placeholder="Personality, allergies..." /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-dog" onClick={submit} disabled={saving} className="rounded-full">{saving ? "Saving..." : "Add dog"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
